<template>
    <div class="operation-step-form">
        <section v-if="step.operation === 'update_tags'" class="operation-section">
            <div class="operation-section__header">
                <h3>{{ $tt('标签字段与值') }}</h3>
            </div>
            <div v-for="field in tagFields" :key="field.id" class="tag-field">
                <label class="label cursor-pointer justify-start gap-2 py-1">
                    <input :checked="tagField(field.id).enabled" type="checkbox" class="checkbox checkbox-sm"
                        @change="setTagFieldEnabled(field.id, $event.target.checked)" />
                    <span class="label-text">{{ $tt(field.name) }}</span>
                </label>
                <div v-if="tagField(field.id).enabled && isArtworkTagField(field.id)"
                    class="tag-artwork-uploader">
                    <div class="tag-artwork-uploader__drop" @click="triggerTagArtworkUpload">
                        <div v-if="tagArtworkPreview" class="tag-artwork-uploader__preview">
                            <img :src="tagArtworkPreview" :alt="$tt('专辑封面')" />
                            <button class="btn btn-circle btn-xs btn-error tag-artwork-uploader__remove"
                                type="button" :title="$tt('删除')" :aria-label="$tt('删除')"
                                @click.stop="removeTagArtwork">×</button>
                        </div>
                        <div v-else class="tag-artwork-uploader__empty">
                            <span class="tag-artwork-uploader__icon">+</span>
                            <span>{{ $tt('点击上传或拖拽图片到此处') }}</span>
                            <small>{{ $tt('支持 JPG, PNG, GIF (最大 5MB)') }}</small>
                        </div>
                        <div v-if="isUploadingTagArtwork" class="tag-artwork-uploader__loading">
                            <span class="loading loading-spinner loading-md"></span>
                            <span>{{ $tt('上传中...') }}</span>
                        </div>
                    </div>
                    <input ref="tagArtworkInput" type="file" accept="image/*" class="hidden"
                        :disabled="isUploadingTagArtwork" @change="handleTagArtworkChange" />
                    <runtime-value v-if="showRuntimeControls"
                        :value="tagField(field.id).runtime_key" :input-schema="inputSchema"
                        :show-runtime-controls="showRuntimeControls"
                        :value-input="tagField(field.id).value" :placeholder="$tt('请输入图片地址')"
                        @input="setTagFieldRuntimeKey(field.id, $event)"
                        @value-input="setTagFieldValue(field.id, $event)" />
                </div>
                <runtime-value v-else-if="tagField(field.id).enabled"
                    :value="tagField(field.id).runtime_key" :input-schema="inputSchema"
                    :show-runtime-controls="showRuntimeControls" :multiline="isTextareaTagField(field.id)"
                    :value-input="tagField(field.id).value" :placeholder="$tt('请输入值')"
                    @input="setTagFieldRuntimeKey(field.id, $event)"
                    @value-input="setTagFieldValue(field.id, $event)" />
            </div>
            <div class="operation-subsection">
                <div class="operation-subsection__header">{{ $tt('歌词') }}</div>
                <div class="lyrics-switch-grid">
                    <label v-for="action in lyricActions" :key="action.id" class="lyrics-switch-card">
                        <div class="lyrics-switch-card__head">
                            <div class="flex-1 min-w-0 pr-3">
                                <span class="label-text font-medium block">{{ $tt(action.name) }}</span>
                                <div class="text-xs text-base-content/60 mt-1 leading-5">{{ $tt(action.description) }}</div>
                            </div>
                            <input :checked="tagActionField(action.id).enabled" type="checkbox"
                                class="toggle toggle-sm toggle-primary shrink-0"
                                @change="setTagFieldEnabled(action.id, $event.target.checked)" />
                        </div>
                    </label>
                </div>
            </div>
            <div class="operation-subsection">
                <div class="operation-subsection__header">{{ $tt('图片') }}</div>
                <div class="lyrics-switch-grid">
                    <label v-for="action in artworkActions" :key="action.id" class="lyrics-switch-card">
                        <div class="lyrics-switch-card__head">
                            <div class="flex-1 min-w-0 pr-3">
                                <span class="label-text font-medium block">{{ $tt(action.name) }}</span>
                                <div class="text-xs text-base-content/60 mt-1 leading-5">{{ $tt(action.description) }}</div>
                            </div>
                            <input :checked="tagActionField(action.id).enabled" type="checkbox"
                                class="toggle toggle-sm toggle-primary shrink-0"
                                @change="setTagFieldEnabled(action.id, $event.target.checked)" />
                        </div>
                    </label>
                </div>
            </div>
        </section>

        <section v-else-if="step.operation === 'replace_text'" class="operation-section">
            <div class="operation-section__header">
                <h3>{{ $tt('替换文本') }}</h3>
            </div>
            <div class="operation-info-box">
                {{ $tt('清除隐藏在元数据中的无关信息，支持文本替换和正则表达式替换两种模式。') }}
            </div>
            <div class="form-control operation-field">
                <label class="operation-field__label">{{ $tt('替换模式') }}</label>
                <div class="text-xs text-base-content/60">{{ $tt('选择文本替换或正则表达式替换') }}</div>
                <select v-model="step.params.mode" class="select select-bordered select-sm">
                    <option value="text">{{ $tt('全文替换') }}</option>
                    <option value="regex">{{ $tt('正则替换') }}</option>
                </select>
            </div>
            <div class="form-control operation-field">
                <label class="operation-field__label">{{ $tt('修改范围') }}</label>
                <div class="text-xs text-base-content/60">{{ $tt('选择要修改的元数据字段') }}</div>
                <multi-select v-model="step.params.fields" :options="textFieldOptions" />
            </div>
            <div v-for="(rule, index) in step.params.replace_text_list" :key="`${step.key || 'step'}-replace-${index}`"
                class="rule-card">
                <div class="rule-card__head"><strong>{{ $tt('替换规则') }} {{ index + 1 }}</strong><button
                        v-if="step.params.replace_text_list.length > 1" class="btn btn-ghost btn-xs btn-circle"
                        type="button" :title="$tt('删除')" :aria-label="$tt('删除')"
                        @click="step.params.replace_text_list.splice(index, 1)">×</button></div>
                <div class="operation-grid operation-grid--two">
                    <runtime-value v-model="rule.before_runtime_key" :input-schema="inputSchema"
                        :show-runtime-controls="showRuntimeControls"
                        :value-input="rule.before" :placeholder="$tt('替换前')" @value-input="rule.before = $event" />
                    <runtime-value v-model="rule.after_runtime_key" :input-schema="inputSchema"
                        :show-runtime-controls="showRuntimeControls"
                        :value-input="rule.after" :placeholder="$tt('替换后')" @value-input="rule.after = $event" />
                </div>
            </div>
            <button class="operation-add-button" type="button"
                @click="step.params.replace_text_list.push({ before: '', after: '' })">{{ $tt('添加替换规则') }}</button>
            <div class="operation-hint-box">
                {{ $tt('支持普通文本匹配和正则表达式匹配（根据替换模式）') }}
            </div>
        </section>

        <section v-else-if="step.operation === 'tidy_folder'" class="operation-section">
            <div class="operation-section__header">
                <h3>{{ $tt('整理文件') }}</h3>
            </div>
            <div class="operation-info-box">{{ $tt('整理文件夹，按一级目录、二级目录选定的信息分类。') }}</div>
            <runtime-value v-model="step.params.root_path_runtime_key" :input-schema="inputSchema"
                :show-runtime-controls="showRuntimeControls"
                :value-input="step.params.root_path" :placeholder="$tt('请输入根目录路径')"
                @value-input="step.params.root_path = $event" />
            <div class="form-control">
                <label class="label"><span class="label-text">{{ $tt('目录层级设置') }}</span></label>
                <div v-for="(item, index) in step.params.dir_list" :key="`${step.key || 'step'}-dir-${index}`"
                    class="tidy-row" :class="{ 'tidy-row--custom': item.value === 'custom' }">
                    <select v-model="item.value" class="select select-bordered select-sm">
                        <option v-for="field in tidyFields" :key="field.id" :value="field.id">{{ $tt(field.name) }}
                        </option>
                    </select>
                    <input v-if="item.value === 'custom'" v-model="item.value2" class="input input-bordered input-sm"
                        :placeholder="$tt('请输入自定义变量，例如：${artist}-${album}')" type="text" />
                    <button class="btn btn-ghost btn-xs btn-circle text-error" type="button" :title="$tt('删除')"
                        :aria-label="$tt('删除')" :disabled="step.params.dir_list.length <= 1"
                        @click="step.params.dir_list.splice(index, 1)">×</button>
                </div>
                <button class="operation-add-button" type="button"
                    @click="step.params.dir_list.push({ value: '', value2: '' })">{{ $tt('添加目录层级') }}</button>
            </div>
            <div class="form-control">
                <label class="label cursor-pointer justify-start gap-3 operation-toggle-field">
                    <input v-model="step.params.rule_form.is_clean_music" type="checkbox"
                        class="toggle toggle-warning" />
                    <span class="operation-toggle-field__content">
                        <span class="label-text">{{ $tt('启用音乐洗版') }}</span>
                        <small>{{ $tt('音乐洗版就是按以下规则换更好的版本') }}</small>
                    </span>
                </label>
            </div>
            <div v-if="step.params.rule_form.is_clean_music" class="clean-rule-panel">
                <div class="operation-subsection__header">{{ $tt('洗版规则配置') }}</div>
                <div class="form-control">
                    <label class="label"><span class="label-text">{{ $tt('格式优先级') }}</span></label>
                    <multi-select v-model="step.params.rule_form.clean_format_priority"
                        :options="translateOptions(formatList)" />
                </div>
                <div class="operation-grid operation-grid--two">
                    <div class="form-control">
                        <label class="label"><span class="label-text">{{ $tt('文件大小规则') }}</span></label>
                        <single-select v-model="step.params.rule_form.clean_size_rule"
                            :options="translateOptions(sizeRuleList)" :placeholder="$tt('请选择')" />
                    </div>
                    <div class="form-control">
                        <label class="label"><span class="label-text">{{ $tt('比特率规则') }}</span></label>
                        <single-select v-model="step.params.rule_form.clean_bitrate_rule"
                            :options="translateOptions(bitRuleList)" :placeholder="$tt('请选择')" />
                    </div>
                    <div class="form-control">
                        <label class="label"><span class="label-text">{{ $tt('采样率/位深规则') }}</span></label>
                        <single-select v-model="step.params.rule_form.clean_sample_rule"
                            :options="translateOptions(sampleRuleList)" :placeholder="$tt('请选择')" />
                    </div>
                    <div class="form-control">
                        <label class="label"><span class="label-text">{{ $tt('文件名规则') }}</span></label>
                        <single-select v-model="step.params.rule_form.clean_name_rule"
                            :options="translateOptions(nameRuleList)" :placeholder="$tt('请选择')" />
                    </div>
                    <div class="form-control">
                        <label class="label"><span class="label-text">{{ $tt('删除策略') }}</span></label>
                        <single-select v-model="step.params.rule_form.clean_delete_rule"
                            :options="translateOptions(deleteRuleList)" :placeholder="$tt('请选择')" />
                    </div>
                </div>
            </div>
        </section>

        <section v-else-if="step.operation === 'remove_null_folder'" class="operation-section">
            <div class="operation-section__header">
                <h3>{{ $tt('删除文件夹') }}</h3>
            </div>
            <div class="operation-info-box">{{ $tt('此操作将会删除选中的文件夹，请谨慎选择删除模式') }}</div>
            <div class="operation-subsection">
                <div class="operation-subsection__header">{{ $tt('删除模式') }}</div>
                <div class="delete-mode-options">
                    <label v-for="mode in deleteModeOptions" :key="mode.id" class="delete-mode-option"
                        :class="{ 'delete-mode-option--danger': mode.id === 'all' }">
                        <input v-model="step.params.delete_mode" type="radio"
                            :class="mode.id === 'all' ? 'radio radio-error radio-sm' : 'radio radio-primary radio-sm'"
                            :value="mode.id" />
                        <span>
                            <strong :class="{ 'text-error': mode.id === 'all' }">{{ $tt(mode.name) }}</strong>
                            <small :class="{ 'text-error/70': mode.id === 'all' }">{{ $tt(mode.description) }}</small>
                        </span>
                    </label>
                </div>
            </div>
        </section>

        <section v-else-if="step.operation === 'format_music'" class="operation-section">
            <div class="operation-section__header">
                <h3>{{ $tt('格式转换') }}</h3>
            </div>
            <div class="operation-info-box">
                <p>{{ $tt('将音频转换成其他格式，保存在同目录下') }}</p>
                <p>{{ $tt('不会删除源文件，安全可靠') }}</p>
                <p>{{ $tt('需要下载 ffmpeg 可执行文件') }}</p>
                <p>{{ $tt('放置在挂载的 /app/data/bin/ 目录下') }}</p>
            </div>
            <div class="operation-subsection">
                <div class="operation-subsection__header">{{ $tt('输入设置') }}</div>
                <div class="form-control operation-field">
                    <label class="operation-field__label">{{ $tt('输入格式') }}</label>
                    <div class="text-xs text-base-content/60">{{ $tt('选择需要转换的音频格式') }}</div>
                    <multi-select v-model="step.params.input_formats" :options="translateOptions(inputFormatOptions)"
                        :placeholder="$tt('留空则转换所有格式')" />
                    <div class="text-xs text-base-content/50">{{ $tt('留空则对所有格式进行转换') }}</div>
                </div>
            </div>
            <div class="operation-subsection">
                <div class="operation-subsection__header">{{ $tt('格式设置') }}</div>
                <div class="operation-grid operation-grid--two">
                    <div class="form-control operation-field">
                        <label class="operation-field__label">{{ $tt('目标格式') }}</label>
                        <div class="text-xs text-base-content/60">{{ $tt('选择音频输出格式') }}</div>
                        <select v-model="step.params.music_type" class="select select-bordered select-sm">
                            <option v-for="option in outputFormatOptions" :key="`format-${option.id}`"
                                :value="option.id">{{ $tt(option.name) }}</option>
                        </select>
                    </div>
                    <div class="form-control operation-field">
                        <label class="operation-field__label">{{ $tt('比特率') }}</label>
                        <div class="text-xs text-base-content/60">{{ $tt('音频质量设置') }}</div>
                        <select v-model="step.params.bit_rate" class="select select-bordered select-sm">
                            <option v-for="option in bitRateOptions" :key="`bitrate-${option.id}`"
                                :value="option.id">{{ $tt(option.name) }}</option>
                        </select>
                        <div v-if="step.params.bit_rate === 'custom'" class="format-custom-value">
                            <input v-model="step.params.bit_rate_custom" type="number" min="1" max="1000"
                                class="input input-bordered input-sm font-mono" placeholder="128" />
                            <span>kbps</span>
                        </div>
                    </div>
                    <div class="form-control operation-field">
                        <label class="operation-field__label">{{ $tt('采样率') }}</label>
                        <div class="text-xs text-base-content/60">{{ $tt('音频采样频率') }}</div>
                        <select v-model="step.params.sample_rate" class="select select-bordered select-sm">
                            <option v-for="option in sampleRateOptions" :key="`samplerate-${option.id}`"
                                :value="option.id">{{ $tt(option.name) }}</option>
                        </select>
                        <div v-if="step.params.sample_rate === 'custom'" class="format-custom-value">
                            <input v-model="step.params.sample_rate_custom" type="number" min="1" max="192000"
                                class="input input-bordered input-sm font-mono" placeholder="44100" />
                            <span>Hz</span>
                        </div>
                        <div v-if="step.params.sample_rate === 'custom'" class="text-xs text-base-content/50">
                            {{ $tt('必须是目标格式支持的有效采样率值，否则将转换失败') }}
                        </div>
                    </div>
                </div>
            </div>
        </section>

        <section v-else-if="step.operation === 'split_cue'" class="operation-section">
            <div class="operation-section__header">
                <h3>{{ $tt('分割音轨') }}</h3>
            </div>
            <div class="operation-info-box">
                <p>{{ $tt('使用 CUE 标记文件无损切割整轨 CD 音轨') }}</p>
                <p>{{ $tt('不会删除源文件（除非手动选择）') }}</p>
                <p>{{ $tt('需要下载 ffmpeg 可执行文件') }}</p>
                <p>{{ $tt('放置在挂载的 /app/data/bin/ 目录下') }}</p>
            </div>
            <div class="operation-subsection">
                <div class="operation-subsection__header">{{ $tt('输出设置') }}</div>
                <div class="form-control operation-field">
                    <label class="operation-field__label">{{ $tt('目标格式') }}</label>
                    <div class="text-xs text-base-content/60">{{ $tt('选择切割后的音频格式') }}</div>
                    <select v-model="step.params.target_format" class="select select-bordered select-sm">
                        <option v-for="option in outputFormatOptions" :key="`cue-format-${option.id}`"
                            :value="option.id">{{ $tt(option.name) }}</option>
                    </select>
                </div>
                <label class="label cursor-pointer justify-start gap-3 operation-toggle-field">
                    <input v-model="step.params.is_delete_source" type="checkbox" class="toggle toggle-warning" />
                    <span class="operation-toggle-field__content">
                        <span class="label-text">{{ $tt('删除源文件') }}</span>
                        <small>{{ $tt('切割完成后是否删除原始文件') }}</small>
                    </span>
                </label>
            </div>
        </section>

        <section v-else-if="step.operation === 'import_music'" class="operation-section">
            <div class="operation-section__header">
                <h3>{{ $tt('导入收藏') }}</h3>
            </div>
            <div class="operation-info-box">
                <p>{{ $tt('将勾选的音乐文件导入音乐收藏库') }}</p>
                <p>{{ $tt('支持批量导入和智能去重') }}</p>
                <p>{{ $tt('导入速率可调节，适应不同性能的机器') }}</p>
            </div>
            <div class="operation-subsection">
                <div class="operation-subsection__header">{{ $tt('导入设置') }}</div>
                <label class="label cursor-pointer justify-start gap-3 operation-toggle-field">
                    <input v-model="step.params.is_skip_exist" type="checkbox" class="toggle toggle-primary" />
                    <span class="operation-toggle-field__content">
                        <span class="label-text">{{ $tt('跳过已入库文件') }}</span>
                        <small>{{ $tt('自动跳过已经存在于收藏库中的音乐文件') }}</small>
                    </span>
                </label>
                <div class="form-control operation-field">
                    <label class="operation-field__label">{{ $tt('导入速率') }}</label>
                    <div class="text-xs text-base-content/60">{{ $tt('每秒导入的音乐文件数量') }}</div>
                    <div class="format-custom-value">
                        <input v-model="step.params.import_rate" type="number" min="1" max="100"
                            class="input input-bordered input-sm font-mono" placeholder="10" />
                        <span>{{ $tt('文件/秒') }}</span>
                    </div>
                    <div class="text-xs text-base-content/50">
                        {{ $tt('建议根据机器性能调节，数值过大可能影响系统稳定性') }}
                    </div>
                </div>
            </div>
        </section>

        <section v-else-if="step.operation === 'duplicate_check'" class="operation-section">
            <div class="operation-section__header">
                <h3>{{ $tt('重复文件检查') }}</h3>
            </div>
            <div class="operation-info-box">
                <p>{{ $tt('智能查找重复的音乐文件，支持多种检测方式') }}</p>
                <p>{{ $tt('不会自动删除文件，安全可靠') }}</p>
                <p>{{ $tt('可在操作记录中查看详细结果并进行后续处理') }}</p>
                <p>{{ $tt('支持 Hash 校验和文件名相似度检测') }}</p>
            </div>
            <div class="operation-subsection">
                <div class="operation-subsection__header">{{ $tt('检测方式') }}</div>
                <div class="simple-feature-list">
                    <div class="simple-feature-item">
                        <strong>{{ $tt('Hash 校验') }}</strong>
                        <small>{{ $tt('基于文件内容哈希值检测完全相同的文件') }}</small>
                    </div>
                    <div class="simple-feature-item">
                        <strong>{{ $tt('文件名校验') }}</strong>
                        <small>{{ $tt('基于文件名相似度检测可能重复的文件') }}</small>
                    </div>
                </div>
            </div>
            <div class="operation-subsection">
                <div class="operation-subsection__header">{{ $tt('高级选项') }}</div>
                <label class="label cursor-pointer justify-start gap-3 operation-toggle-field">
                    <input v-model="step.params.is_acoustid" type="checkbox" class="toggle toggle-warning" />
                    <span class="operation-toggle-field__content">
                        <span class="label-text">{{ $tt('启用声纹重复校验') }}</span>
                        <small>{{ $tt('通过音频特征识别重复文件，精度更高但速度相对较慢') }}</small>
                    </span>
                </label>
                <div v-if="step.params.is_acoustid" class="operation-warning-box">
                    <p>{{ $tt('启用声纹识别后检测速度会显著降低，请耐心等待') }}</p>
                </div>
                <label class="label cursor-pointer justify-start gap-3 operation-toggle-field">
                    <input v-model="step.params.is_metadata_compare" type="checkbox" class="toggle toggle-info" />
                    <span class="operation-toggle-field__content">
                        <span class="label-text">{{ $tt('启用音乐元数据对比') }}</span>
                        <small>{{ $tt('基于音乐收藏中标题、艺术家、专辑等元数据识别重复文件') }}</small>
                    </span>
                </label>
            </div>
            <div class="operation-hint-box">{{ $tt('系统只会检测重复文件，不会自动删除任何内容') }}</div>
        </section>

        <section v-else-if="step.operation === 'replay_gain'" class="operation-section">
            <div class="operation-section__header">
                <h3>{{ $tt('回放增益') }}</h3>
            </div>
            <div class="operation-info-box">
                <p>{{ $tt('通过扫描各个文件的音量大小，计算回放增益值') }}</p>
                <p>{{ $tt('将回放增益信息写入文件的属性标签中') }}</p>
                <p>{{ $tt('播放时通过回放增益统一音量大小') }}</p>
                <p>{{ $tt('提供更一致的听音体验') }}</p>
            </div>
            <div class="operation-subsection">
                <div class="operation-subsection__header">{{ $tt('环境要求') }}</div>
                <div class="operation-warning-box">
                    <strong>{{ $tt('需要 FFmpeg 支持') }}</strong>
                    <p>{{ $tt('下载 FFmpeg 可执行文件') }}</p>
                    <p>{{ $tt('放置在挂载的 /app/data/bin/ 目录下') }}</p>
                    <p>{{ $tt('确保文件具有执行权限') }}</p>
                </div>
            </div>
            <div class="operation-hint-box">{{ $tt('处理过程可能需要较长时间，请耐心等待') }}</div>
        </section>

        <section v-else-if="step.operation === 'generate_strm'" class="operation-section">
            <div class="operation-section__header">
                <h3>{{ $tt('生成strm文件') }}</h3>
            </div>
            <div class="operation-info-box">
                <p>{{ $tt('将音乐文件转换为 STRM 流媒体文件') }}</p>
                <p>{{ $tt('适用于媒体服务器和播放器集成') }}</p>
                <p>{{ $tt('支持签名验证，提高安全性') }}</p>
                <p>{{ $tt('可自定义保存位置') }}</p>
            </div>
            <div class="operation-subsection">
                <div class="operation-subsection__header">{{ $tt('输出设置') }}</div>
                <div class="form-control operation-field">
                    <label class="operation-field__label">{{ $tt('保存目录') }}</label>
                    <div class="text-xs text-base-content/60">{{ $tt('指定STRM文件的保存位置') }}</div>
                    <input v-model="step.params.save_dir" type="text" class="input input-bordered input-sm"
                        :placeholder="$tt('默认保存到当前目录')" />
                    <div class="text-xs text-base-content/50">{{ $tt('留空则保存到当前目录') }}</div>
                </div>
                <label class="label cursor-pointer justify-start gap-3 operation-toggle-field">
                    <input v-model="step.params.is_sign" type="checkbox" class="toggle toggle-primary" />
                    <span class="operation-toggle-field__content">
                        <span class="label-text">{{ $tt('启用签名验证') }}</span>
                        <small>{{ $tt('为OpenList提供签名验证支持') }}</small>
                    </span>
                </label>
                <div v-if="step.params.is_sign" class="operation-warning-box">
                    <p>{{ $tt('开启签名后扫描速度会有所降低') }}</p>
                </div>
            </div>
        </section>

        <section v-else-if="step.operation === 'file_index'" class="operation-section">
            <div class="operation-section__header">
                <h3>{{ $tt('文件索引管理') }}</h3>
            </div>
            <div class="operation-info-box">
                <p><strong>{{ $tt('增量索引：') }}</strong>{{ $tt('只扫描新增或修改的文件') }}</p>
                <p><strong>{{ $tt('重建索引：') }}</strong>{{ $tt('完全重新扫描所有文件') }}</p>
                <p>{{ $tt('索引完成后可在音乐库中查看和管理文件') }}</p>
            </div>
            <div class="operation-subsection">
                <div class="operation-subsection__header">{{ $tt('索引类型') }}</div>
                <div class="delete-mode-options">
                    <label v-for="mode in fileIndexModeOptions" :key="mode.id" class="delete-mode-option"
                        :class="{ 'delete-mode-option--danger': mode.id === 'rebuild' }">
                        <input v-model="step.params.mode" type="radio"
                            :class="mode.id === 'rebuild' ? 'radio radio-warning radio-sm' : 'radio radio-primary radio-sm'"
                            :value="mode.id" />
                        <span>
                            <strong>{{ $tt(mode.name) }}</strong>
                            <small>{{ $tt(mode.description) }}</small>
                        </span>
                    </label>
                </div>
            </div>
            <div class="operation-warning-box">
                <p>{{ $tt('重建索引会重新处理所有文件，请根据文件数量预留充足时间') }}</p>
            </div>
        </section>

        <section v-else-if="step.operation === 'convert_chinese'" class="operation-section">
            <div class="operation-section__header">
                <h3>{{ $tt('语言转换') }}</h3>
            </div>
            <div class="operation-info-box">
                {{ $tt('自动识别简体中文和繁体中文，支持双向转换。适用于音乐标签中的艺术家、专辑、歌曲名等字段的字体转换。') }}
            </div>
            <div class="operation-subsection">
                <div class="operation-subsection__header">{{ $tt('转换方向') }}</div>
                <div class="delete-mode-options">
                    <label v-for="mode in languageModeOptions" :key="mode.id" class="delete-mode-option">
                        <input v-model="step.params.mode" type="radio" class="radio radio-primary radio-sm"
                            :value="mode.id" />
                        <span>
                            <strong>{{ $tt(mode.name) }}</strong>
                            <small>{{ $tt(mode.description) }}</small>
                        </span>
                    </label>
                </div>
            </div>
            <div class="operation-subsection">
                <div class="operation-subsection__header">{{ $tt('修改范围') }}</div>
                <multi-select v-model="step.params.fields" :options="textFieldOptions" />
                <div class="text-xs text-base-content/60">{{ $tt('选择需要进行语言转换的元数据字段') }}</div>
            </div>
            <div class="operation-subsection">
                <div class="operation-subsection__header">{{ $tt('转换预览') }}</div>
                <div class="language-preview">
                    <div>
                        <strong>{{ $tt('转换前') }}</strong>
                        <span>{{ step.params.mode === 'sim2trad' ? $tt('华语流行') : $tt('華語流行') }}</span>
                    </div>
                    <div>
                        <strong>{{ $tt('转换后') }}</strong>
                        <span class="text-primary">{{ step.params.mode === 'sim2trad' ? $tt('華語流行') : $tt('华语流行') }}</span>
                    </div>
                </div>
            </div>
        </section>

        <section v-else-if="step.operation === 'encoding_fix'" class="operation-section">
            <div class="operation-section__header">
                <h3>{{ $tt('乱码修复') }}</h3>
            </div>
            <div class="operation-warning-box">
                <strong>{{ $tt('编码乱码修复') }}</strong>
                <p>{{ $tt('智能检测并修复音乐文件元数据中的字符编码问题，支持GB2312、GBK、UTF-8等多种编码格式的自动转换。') }}</p>
            </div>
            <div class="operation-subsection">
                <div class="operation-subsection__header">{{ $tt('检测范围') }}</div>
                <multi-select v-model="step.params.fields" :options="textFieldOptions" />
                <div class="text-xs text-base-content/60">{{ $tt('选择需要检测和修复乱码的元数据字段') }}</div>
            </div>
            <div class="operation-subsection">
                <div class="operation-subsection__header">{{ $tt('常见问题') }}</div>
                <div class="simple-feature-list">
                    <div class="simple-feature-item"><strong>{{ $tt('乱码类型示例') }}</strong><small>ä¸­æ → {{ $tt('中文') }}</small></div>
                    <div class="simple-feature-item"><strong>{{ $tt('乱码类型示例') }}</strong><small>??? → {{ $tt('汉字') }}</small></div>
                    <div class="simple-feature-item"><strong>{{ $tt('乱码类型示例') }}</strong><small>ÖÐÎÄ → {{ $tt('中文') }}</small></div>
                </div>
            </div>
        </section>

        <section v-else-if="step.operation === 'auto_tag'" class="operation-section">
            <div class="operation-section__header">
                <h3>{{ $tt('自动刮削') }}</h3>
            </div>
            <div class="operation-info-box">
                <p>{{ $tt('根据元数据中的标题、艺术家、专辑和文件名、文件完整路径进行匹配。') }}</p>
                <p>{{ $tt('标题、艺术家、专辑每一项匹配等级指标：完全一致，包含，不匹配') }}</p>
            </div>
            <div class="form-control">
                <label class="label"><span class="label-text">{{ $tt('匹配模式') }}</span></label>
                <div class="auto-mode-options">
                    <label v-for="mode in autoModeOptions" :key="mode.id" class="auto-mode-option">
                        <input v-model="step.params.mode" type="radio" class="radio radio-primary radio-sm"
                            :value="mode.id" />
                        <span>
                            <strong>{{ $tt(mode.name) }}</strong>
                            <small>{{ $tt(mode.description) }}</small>
                        </span>
                    </label>
                </div>
            </div>
            <field-multi-select v-model="step.params.source_list" :options="sourceOptions" :label="$tt('标签源')" />
            <field-multi-select v-model="step.params.modify_list" :options="modifyLists" :label="$tt('修改字段')" />
            <toggle-field v-model="step.params.is_skip_tag" :label="$tt('跳过匹配')"
                :description="$tt('如修改范围中元数据都存在，则跳过匹配，原有音乐标签将被保留，不会被修改或覆盖')" />
            <toggle-field v-model="step.params.is_folder_album" :label="$tt('同目录解释为同个专辑')"
                :description="$tt('当元数据中不存在专辑信息，用于补充专辑信息，提高匹配度')" />
        </section>

        <section v-else-if="step.operation === 'background_scrape'" class="operation-section">
            <div class="operation-section__header">
                <h3>{{ $tt('后台刮削') }}</h3>
            </div>
            <div class="operation-info-box">
                <strong>{{ $tt('后台刮削配置说明') }}</strong>
                <p>{{ $tt('后台刮削功能的表单配置与系统设置中的后台刮削配置完全一致。') }}</p>
                <p>{{ $tt('如需修改配置,请前往系统设置。') }}</p>
            </div>
            <div class="background-scrape-features">
                <div v-for="feature in backgroundScrapeFeatures" :key="feature.name" class="background-scrape-feature">
                    <strong>{{ $tt(feature.name) }}</strong>
                    <small>{{ $tt(feature.description) }}</small>
                </div>
            </div>
            <div class="operation-warning-box">
                <strong>{{ $tt('注意事项') }}</strong>
                <p>{{ $tt('操作台的后台刮削只对勾选的文件进行刮削') }}</p>
            </div>
        </section>

        <section v-else-if="step.operation === 'split_filename'" class="operation-section">
            <div class="operation-section__header">
                <h3>{{ $tt('拆分元数据') }}</h3>
            </div>
            <div class="operation-info-box">
                {{ $tt('通过正则表达式匹配规则，从文件名或其他元数据中提取出艺术家、专辑、标题等信息，并自动填充到对应的标签字段中。') }}
            </div>
            <div class="form-control operation-field">
                <label class="operation-field__label">{{ $tt('提取源') }}</label>
                <div class="text-xs text-base-content/60">{{ $tt('选择要从哪个字段提取元数据信息') }}</div>
                <select v-model="step.params.extract_field" class="select select-bordered select-sm">
                    <option value="filename">{{ $tt('文件名') }}</option>
                    <option value="title">{{ $tt('标题') }}</option>
                    <option value="lyrics">{{ $tt('歌词') }}</option>
                </select>
            </div>
            <div class="form-control operation-field">
                <label class="operation-field__label">{{ $tt('拆分规则') }}</label>
                <div class="text-xs text-base-content/60">{{ $tt('每行一条正则规则，命名分组会写入对应元数据字段') }}</div>
                <textarea v-model="step.params.rule_list_text" class="textarea textarea-bordered w-full font-mono" rows="5"
                    :placeholder="$tt('每行一条正则规则')"></textarea>
                <div class="operation-hint-box">
                    {{ $tt('例如：(?P\u003Ctracknumber>\\d+)\\.\\s*(?P\u003Ctitle>.+)') }}
                </div>
            </div>
        </section>

        <section v-else class="operation-section">
            <div class="operation-section__header">
                <h3>{{ operationTitle }}</h3>
            </div>
            <div v-for="field in genericParams" :key="field.key" class="form-control operation-field">
                <field-multi-select v-if="field.type === 'tag-list'" v-model="step.params[field.key]"
                    :options="tagOptions" :label="$tt(field.label)" />
                <field-multi-select v-else-if="field.type === 'list' && field.options" v-model="step.params[field.key]"
                    :options="optionList(field.options)" :label="$tt(field.label)" />
                <toggle-field v-else-if="field.type === 'boolean'" v-model="step.params[field.key]"
                    :label="$tt(field.label)" />
                <template v-else>
                    <label class="operation-field__label" :for="`operation-${step.key || 'step'}-${field.key}`">{{
                        $tt(field.label) }}</label>
                    <select v-if="field.type === 'select'" :id="`operation-${step.key || 'step'}-${field.key}`"
                        v-model="step.params[field.key]" class="select select-bordered select-sm">
                        <option v-for="option in field.options" :key="String(option)" :value="option">{{ option ||
                            $tt('保持原样') }}</option>
                    </select>
                    <input v-else-if="field.type === 'number' || field.type === 'path'"
                        :id="`operation-${step.key || 'step'}-${field.key}`" v-model="step.params[field.key]"
                        :type="field.type === 'number' ? 'number' : 'text'" class="input input-bordered input-sm"
                        :placeholder="$tt(field.label)" />
                    <textarea v-else :id="`operation-${step.key || 'step'}-${field.key}`"
                        class="textarea textarea-bordered w-full font-mono" rows="3"
                        :value="jsonValue(step.params[field.key])" :placeholder="$tt(field.label)"
                        @input="setJsonValue(field.key, $event.target.value)"></textarea>
                </template>
            </div>
        </section>
    </div>
</template>

<script>
import SingleSelect from '@/components/iview/SingleSelect'
import MultiSelect from '@/components/iview/MultiSelect'
import RuntimeValue from './RuntimeValue.vue'
import FieldMultiSelect from './FieldMultiSelect.vue'
import ToggleField from './ToggleField.vue'
import { TAG_FIELDS, modifyListBatch, TIDY_FIELDS, UPDATE_TAG_ACTIONS, DEFAULT_TIDY_RULE_FORM, getBatchOperationDefinition } from './batchOperationCatalog'

export default {
    name: 'OperationStepForm',
    components: { RuntimeValue, FieldMultiSelect, ToggleField, SingleSelect, MultiSelect },
    props: {
        step: { type: Object, required: true },
        inputSchema: { type: Array, default: () => [] },
        showRuntimeControls: { type: Boolean, default: true }
    },
    errorCaptured(error, vm, info) {
        console.error('[OperationStepForm] 子组件渲染失败', {
            error,
            info,
            operation: this.step && this.step.operation,
            params: this.step && this.step.params
        })
        return false
    },
    data() {
        return {
            uploadUrl: '/apimt/upload_image/',
            isUploadingTagArtwork: false,
            tagArtworkLocalPreview: '',
            sourceOptions: [
                { id: 'netease', name: '网易云音乐' },
                { id: 'qmusic', name: 'QQ音乐' },
                { id: 'migu', name: '咪咕音乐' }
            ],
            autoModeOptions: [
                { id: 'simple', name: '宽松模式', description: '满足标题包含即可，但也优先选完全一致' },
                { id: 'normal', name: '标准模式', description: '三个指标必须为包含或完全一致，但也优先选完全一致' },
                { id: 'hard', name: '严格模式', description: '三个指标必须都为完全一致' }
            ],
            deleteModeOptions: [
                { id: 'null', name: '删除空文件夹', description: '仅删除完全空的文件夹' },
                { id: 'null_music', name: '删除无音乐文件夹', description: '删除不包含音乐文件的文件夹' },
                { id: 'all', name: '删除勾选所有文件夹和文件', description: '危险操作：此操作无法撤销，请谨慎使用' }
            ],
            fileIndexModeOptions: [
                { id: 'incremental', name: '增量索引', description: '快速扫描新增和修改的文件，适合日常维护' },
                { id: 'rebuild', name: '重建索引', description: '完整重新扫描所有文件，耗时较长但数据完整' }
            ],
            languageModeOptions: [
                { id: 'sim2trad', name: '简体转繁体', description: '将简体中文转换为繁体中文' },
                { id: 'trad2sim', name: '繁体转简体', description: '将繁体中文转换为简体中文' }
            ],
            textFields: [
                { id: 'title', name: '标题' },
                { id: 'artist', name: '艺术家' },
                { id: 'album', name: '专辑' },
                { id: 'albumartist', name: '专辑艺术家' },
                { id: 'lyrics', name: '歌词' },
                { id: 'comment', name: '简介' },
                { id: 'description', name: '描述' },
                { id: 'filename', name: '文件名称' },
                { id: 'genre', name: '风格' },
                { id: 'composer', name: '作曲家' },
                { id: 'lyricist', name: '作词家' }
            ],
            outputFormatOptions: [
                { id: '', name: '维持原格式' },
                { id: 'mp3', name: 'MP3' },
                { id: 'wav', name: 'WAV' },
                { id: 'flac', name: 'FLAC' },
                { id: 'wma', name: 'WMA' },
                { id: 'ogg', name: 'OGG' }
            ],
            bitRateOptions: [
                { id: '-1', name: '保持原样' },
                { id: '32', name: '32k' },
                { id: '64', name: '64k' },
                { id: '96', name: '96k' },
                { id: '128', name: '128k' },
                { id: '160', name: '160k' },
                { id: '192', name: '192k' },
                { id: '224', name: '224k' },
                { id: '256', name: '256k' },
                { id: '320', name: '320k' },
                { id: 'custom', name: '自定义' }
            ],
            sampleRateOptions: [
                { id: '-1', name: '保持原样' },
                { id: '11025', name: '11025' },
                { id: '22050', name: '22050' },
                { id: '44100', name: '44100' },
                { id: '48000', name: '48000' },
                { id: 'custom', name: '自定义' }
            ],
            inputFormatOptions: [
                { id: 'flac', name: 'FLAC' },
                { id: 'mp3', name: 'MP3' },
                { id: 'ape', name: 'APE' },
                { id: 'wav', name: 'WAV' },
                { id: 'aiff', name: 'AIFF' },
                { id: 'aif', name: 'AIF' },
                { id: 'tta', name: 'TTA' },
                { id: 'm4a', name: 'M4A' },
                { id: 'ogg', name: 'OGG' },
                { id: 'opus', name: 'OPUS' },
                { id: 'wma', name: 'WMA' },
                { id: 'dsf', name: 'DSF' },
                { id: 'dff', name: 'DFF' },
                { id: 'wmv', name: 'WMV' },
                { id: 'dts', name: 'DTS' },
                { id: 'aac', name: 'AAC' }
            ],
            backgroundScrapeFeatures: [
                { name: '音乐预处理', description: '包括复制文件、声纹识别、乱码修复、繁体转简体等预处理操作' },
                { name: '音乐自动刮削', description: '自动从音乐流媒体平台搜索元数据并进行刮削,支持自动模式和手动模式' },
                { name: '音乐文件整理', description: '按照设定的规则整理音乐文件目录结构,支持洗版功能' }
            ],
            sizeRuleList: [
                { id: 'larger', name: '删除较大文件' },
                { id: 'smaller', name: '删除较小文件' },
                { id: 'none', name: '不判断' }
            ],
            bitRuleList: [
                { id: 'larger', name: '删除较大比特率' },
                { id: 'smaller', name: '删除较小比特率' },
                { id: 'none', name: '不判断' }
            ],
            sampleRuleList: [
                { id: 'larger', name: '删除较大采样率' },
                { id: 'smaller', name: '删除较小采样率' },
                { id: 'none', name: '不判断' }
            ],
            nameRuleList: [
                { id: 'larger', name: '删除含有后缀名' },
                { id: 'smaller', name: '删除不含有后缀名' },
                { id: 'none', name: '不判断' }
            ],
            formatList: [
                { id: 'flac', name: 'FLAC' },
                { id: 'mp3', name: 'MP3' },
                { id: 'ape', name: 'APE' },
                { id: 'wav', name: 'WAV' },
                { id: 'aiff', name: 'AIFF' },
                { id: 'aif', name: 'AIF' },
                { id: 'tta', name: 'TTA' },
                { id: 'm4a', name: 'M4A' },
                { id: 'ogg', name: 'OGG' },
                { id: 'opus', name: 'OPUS' },
                { id: 'wma', name: 'WMA' },
                { id: 'dsf', name: 'DSF' },
                { id: 'dff', name: 'DFF' },
                { id: 'wmv', name: 'WMV' },
                { id: 'dts', name: 'DTS' },
                { id: 'aac', name: 'AAC' }
            ],
            deleteRuleList: [
                { id: 'delete', name: '删除' },
                { id: 'move', name: '移动' }
            ]
        }
    },
    mounted() {
        console.info('[OperationStepForm] 已挂载', {
            operation: this.step && this.step.operation,
            fieldCount: this.step && this.step.params && this.step.params.fields
                ? Object.keys(this.step.params.fields).length
                : 0
        })
        this.loadTagSourcesFromStore()
    },
    watch: {
        step: {
            handler() {
                this.ensureTidyDefaults()
                this.ensureUpdateTagFields()
            },
            immediate: true
        }
    },
    computed: {
        tagFields: () => TAG_FIELDS,
        tidyFields: () => TIDY_FIELDS,
        lyricActions() { return UPDATE_TAG_ACTIONS.filter(action => action.group === 'lyrics') },
        artworkActions() { return UPDATE_TAG_ACTIONS.filter(action => action.group === 'image') },
        tagOptions() { return TAG_FIELDS.map(field => ({ id: field.id, name: this.$tt(field.name) })) },
        modifyLists() { return modifyListBatch.map(field => ({ id: field.id, name: this.$tt(field.name) })) },
        textFieldOptions() { return this.textFields.map(field => ({ id: field.id, name: this.$tt(field.name) })) },
        renameFields() { return TAG_FIELDS.filter(field => !['album_img', 'lyrics_file'].includes(field.id)).concat([{ id: 'original_filename', name: '原文件名' }]) },
        genericDefinition() { return getBatchOperationDefinition(this.step.operation) },
        genericParams() { return this.genericDefinition?.params || [] },
        operationTitle() { return this.genericDefinition?.display_name || this.step.operation },
        tagArtworkPreview() {
            const fields = this.step && this.step.params && this.step.params.fields
            return this.tagArtworkLocalPreview || (fields && fields.album_img && fields.album_img.value) || ''
        }
    },
    methods: {
        async loadTagSourcesFromStore() {
            try {
                const sources = await this.$store.dispatch('loadTagSources')
                const excludeIds = ['smart_tag', 'acoustid', 'musicbrainz']
                this.sourceOptions = sources.filter(plugin => !excludeIds.includes(plugin.id));
            } catch (error) {
                console.error('加载标签源失败:', error)
            }
        },
        ensureUpdateTagFields() {
            if (!this.step || this.step.operation !== 'update_tags') return
            if (!this.step.params) this.$set(this.step, 'params', {})
            const current = this.step.params.fields || {}
            const fields = {}
            TAG_FIELDS.forEach(field => {
                const currentValue = current[field.id]
                if (currentValue && typeof currentValue === 'object' && 'enabled' in currentValue) {
                    fields[field.id] = {
                        enabled: !!currentValue.enabled,
                        value: currentValue.value ?? '',
                        runtime_key: currentValue.runtime_key || ''
                    }
                } else if (currentValue && typeof currentValue === 'object' && currentValue.runtime_key) {
                    fields[field.id] = { enabled: true, value: '', runtime_key: currentValue.runtime_key }
                } else {
                    fields[field.id] = {
                        enabled: currentValue !== undefined && currentValue !== null,
                        value: currentValue ?? '',
                        runtime_key: ''
                    }
                }
            })
            UPDATE_TAG_ACTIONS.forEach(action => {
                fields[action.id] = {
                    enabled: current[action.id] && typeof current[action.id] === 'object'
                        ? !!current[action.id].enabled
                        : !!current[action.id],
                    value: true,
                    runtime_key: ''
                }
            })
            this.$set(this.step.params, 'fields', fields)
        },
        ensureTagFieldsContainer() {
            if (!this.step.params) this.$set(this.step, 'params', {})
            if (!this.step.params.fields || typeof this.step.params.fields !== 'object') {
                this.$set(this.step.params, 'fields', {})
            }
        },
        isTagAction(id) {
            return UPDATE_TAG_ACTIONS.some(action => action.id === id)
        },
        tagField(id) {
            const fields = this.step && this.step.params && this.step.params.fields
            return (fields && fields[id]) || {
                enabled: false,
                value: this.isTagAction(id),
                runtime_key: ''
            }
        },
        ensureTagField(id) {
            this.ensureTagFieldsContainer()
            if (!this.step.params.fields[id]) {
                this.$set(this.step.params.fields, id, {
                    enabled: false,
                    value: this.isTagAction(id),
                    runtime_key: ''
                })
            }
            return this.step.params.fields[id]
        },
        tagActionField(id) {
            return this.tagField(id)
        },
        setTagFieldEnabled(id, enabled) {
            this.$set(this.ensureTagField(id), 'enabled', enabled)
        },
        setTagFieldValue(id, value) {
            this.$set(this.ensureTagField(id), 'value', value)
        },
        setTagFieldRuntimeKey(id, value) {
            this.$set(this.ensureTagField(id), 'runtime_key', value)
        },
        isTextareaTagField(id) {
            return ['lyrics', 'lyrics_file'].includes(id)
        },
        isArtworkTagField(id) {
            return id === 'album_img'
        },
        triggerTagArtworkUpload() {
            if (this.isUploadingTagArtwork) return
            const input = Array.isArray(this.$refs.tagArtworkInput)
                ? this.$refs.tagArtworkInput[0]
                : this.$refs.tagArtworkInput
            if (input) input.click()
        },
        handleTagArtworkChange(event) {
            const file = event.target.files && event.target.files[0]
            if (!file) return
            if (!file.type.startsWith('image/')) {
                this.notify('文件类型错误', '请选择图片文件', 'error')
                event.target.value = ''
                return
            }
            if (file.size > 5 * 1024 * 1024) {
                this.notify('文件过大', '图片大小不能超过5MB', 'error')
                event.target.value = ''
                return
            }
            this.uploadTagArtwork(file, event.target)
        },
        uploadTagArtwork(file, input) {
            const field = this.ensureTagField('album_img')
            const reader = new FileReader()
            reader.onload = event => {
                this.tagArtworkLocalPreview = event.target.result
            }
            reader.readAsDataURL(file)

            const formData = new FormData()
            formData.append('upload_file', file)
            this.isUploadingTagArtwork = true
            const xhr = new XMLHttpRequest()
            xhr.open('POST', this.uploadUrl, true)
            xhr.setRequestHeader('X-CSRFToken', this.getCookie('django_vue_cli_csrftoken'))
            xhr.setRequestHeader('AUTHORIZATION', this.getCookie('AUTHORIZATION'))
            xhr.withCredentials = true
            xhr.onload = () => {
                this.isUploadingTagArtwork = false
                if (input) input.value = ''
                if (xhr.status !== 200) {
                    this.notify('上传失败', `HTTP错误: ${xhr.status}`, 'error')
                    return
                }
                try {
                    const response = JSON.parse(xhr.responseText)
                    if (!response.result) {
                        this.notify('上传失败', response.message || '未知错误', 'error')
                        return
                    }
                    this.$set(field, 'value', response.data)
                    this.$set(field, 'enabled', true)
                    this.tagArtworkLocalPreview = ''
                    this.notify('上传成功', '', 'success')
                } catch (error) {
                    this.notify('上传失败', '服务器响应格式错误', 'error')
                }
            }
            xhr.onerror = () => {
                this.isUploadingTagArtwork = false
                if (input) input.value = ''
                this.notify('上传失败', '网络错误', 'error')
            }
            xhr.send(formData)
        },
        removeTagArtwork() {
            const field = this.ensureTagField('album_img')
            this.$set(field, 'value', '')
            this.$set(field, 'runtime_key', '')
            this.tagArtworkLocalPreview = ''
        },
        notify(title, content, type) {
            if (!this.$myMsg || !this.$myMsg.notify) return
            this.$myMsg.notify({
                title: this.$tt(title),
                content: content ? this.$tt(content) : '',
                type,
                time: 2000
            })
        },
        fieldLabel(id) { return this.renameFields.find(field => field.id === id)?.name || id },
        optionList(options) { return options.map(option => ({ id: option, name: option || this.$tt('保持原样') })) },
        translateOptions(options) { return (options || []).map(item => ({ ...item, name: this.$tt(item.name) })) },
        defaultCleanRuleForm() {
            return JSON.parse(JSON.stringify(DEFAULT_TIDY_RULE_FORM))
        },
        ensureTidyDefaults() {
            if (!this.step || this.step.operation !== 'tidy_folder') return
            if (!this.step.params) this.$set(this.step, 'params', {})
            if (!Array.isArray(this.step.params.dir_list)) {
                this.$set(this.step.params, 'dir_list', [{ value: 'artist', value2: '' }, { value: 'album', value2: '' }])
            }
            this.$set(this.step.params, 'dir_list', this.step.params.dir_list.map(item => {
                if (typeof item === 'string') return { value: item, value2: '' }
                return { value: item.value || item.name || '', value2: item.value2 || '' }
            }))
            const defaults = this.defaultCleanRuleForm()
            if (!this.step.params.rule_form || typeof this.step.params.rule_form !== 'object' || Array.isArray(this.step.params.rule_form)) {
                this.$set(this.step.params, 'rule_form', {})
            }
            Object.keys(defaults).forEach(key => {
                if (this.step.params.rule_form[key] === undefined) {
                    this.$set(this.step.params.rule_form, key, JSON.parse(JSON.stringify(defaults[key])))
                }
            })
        },
        appendField(field) { this.step.params.template_parts.push({ type: 'field', name: field.id, format: '' }) },
        appendText() { const text = String(this.step.params.new_template_text || ''); if (text) { this.step.params.template_parts.push({ type: 'text', value: text }); this.step.params.new_template_text = '' } },
        toggleTemplateMode() {
            if (this.step.params.template_mode === 'advanced') {
                this.step.params.template_parts = this.parseTemplateParts(this.step.params.template || '')
                this.step.params.template = ''
                this.step.params.template_mode = 'builder'
            } else {
                this.step.params.template = this.templatePartsToString(this.step.params.template_parts)
                this.step.params.template_mode = 'advanced'
            }
        },
        templatePartsToString(parts) { return (parts || []).map(part => part.type === 'field' ? `{${part.name}${part.format ? `:${part.format}` : ''}}` : part.value || '').join('') },
        parseTemplateParts(template) {
            const parts = []; let cursor = 0; const pattern = /\{([a-zA-Z0-9_]+)(?::([^}]+))?\}/g; let match
            while ((match = pattern.exec(template))) { if (match.index > cursor) parts.push({ type: 'text', value: template.slice(cursor, match.index) }); parts.push({ type: 'field', name: match[1], format: match[2] || '' }); cursor = match.index + match[0].length }
            if (cursor < template.length) parts.push({ type: 'text', value: template.slice(cursor) }); return parts
        },
        jsonValue(value) { return JSON.stringify(value === undefined ? '' : value, null, 2) },
        setJsonValue(key, value) { try { this.$set(this.step.params, key, JSON.parse(value)) } catch (error) { /* keep invalid JSON editable */ } }
    }
}
</script>

<style scoped>
.operation-step-form,
.operation-section {
    display: grid;
    gap: 12px;
}

.operation-section {
    padding: 4px 0 2px;
}

.operation-section__header {
    display: flex;
    align-items: center;
    min-height: 26px;
    padding-left: 10px;
    border-left: 3px solid oklch(var(--p));
}

.operation-section h3 {
    margin: 0;
    color: oklch(var(--bc));
    font-size: 15px;
    font-weight: 700;
    line-height: 1.25;
}

.operation-grid {
    display: grid;
    gap: 10px;
}

.operation-grid--two {
    grid-template-columns: repeat(2, minmax(0, 1fr));
}

.template-parts {
    display: flex;
    min-height: 46px;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
    padding: 8px;
    border: 1px solid oklch(var(--bc) / .11);
    border-radius: 8px;
    background: oklch(var(--b2) / .46);
    box-shadow: inset 0 1px 0 oklch(var(--b1) / .32);
}

.template-part,
.rule-card {
    border: 1px solid transparent;
    border-radius: 8px;
    font-size: 12px;
}

.template-part {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    min-height: 27px;
    padding: 4px 7px 4px 9px;
    color: oklch(var(--bc) / .82);
    background: oklch(var(--bc) / .06);
}

.template-part--field {
    border-color: oklch(var(--p) / .18);
    color: oklch(var(--p));
    background: oklch(var(--p) / .1);
    font-weight: 650;
}

.template-part button {
    width: 16px;
    height: 16px;
    padding: 0;
    border: 0;
    border-radius: 4px;
    color: currentColor;
    background: transparent;
    cursor: pointer;
    line-height: 1;
}

.template-part button:hover,
.template-part button:focus-visible {
    background: oklch(var(--bc) / .1);
    outline: none;
}

.template-actions {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 6px;
}

.template-actions input {
    width: 120px;
}

.template-mode-switch {
    justify-self: start;
    padding: 2px 0;
    border: 0;
    color: oklch(var(--p));
    background: transparent;
    cursor: pointer;
    font-size: 12px;
    font-weight: 650;
    line-height: 1.5;
}

.template-mode-switch:hover,
.template-mode-switch:focus-visible {
    color: oklch(var(--pf));
    text-decoration: underline;
    outline: none;
}

.tag-field {
    display: grid;
    gap: 7px;
    padding: 8px 10px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: oklch(var(--bc) / .035);
    transition: border-color .18s ease, background-color .18s ease;
}

.tag-field:focus-within {
    border-color: oklch(var(--p) / .28);
    background: oklch(var(--p) / .045);
}

.tag-field .label {
    min-height: 22px;
}

.tag-artwork-uploader {
    display: grid;
    gap: 8px;
}

.tag-artwork-uploader__drop {
    position: relative;
    display: grid;
    place-items: center;
    width: min(100%, 220px);
    aspect-ratio: 1 / 1;
    overflow: hidden;
    border: 2px dashed oklch(var(--bc) / .18);
    border-radius: 8px;
    background: oklch(var(--b1) / .7);
    cursor: pointer;
    transition: border-color .18s ease, background-color .18s ease;
}

.tag-artwork-uploader__drop:hover,
.tag-artwork-uploader__drop:focus-within {
    border-color: oklch(var(--p) / .38);
    background: oklch(var(--b2) / .42);
}

.tag-artwork-uploader__preview {
    position: relative;
    width: 100%;
    height: 100%;
}

.tag-artwork-uploader__preview img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.tag-artwork-uploader__remove {
    position: absolute;
    top: 6px;
    right: 6px;
    z-index: 2;
}

.tag-artwork-uploader__empty {
    display: grid;
    justify-items: center;
    gap: 6px;
    padding: 14px;
    color: oklch(var(--bc) / .58);
    text-align: center;
    font-size: 12px;
    line-height: 1.45;
}

.tag-artwork-uploader__empty small {
    color: oklch(var(--bc) / .42);
}

.tag-artwork-uploader__icon {
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border-radius: 999px;
    color: oklch(var(--p));
    background: oklch(var(--p) / .1);
    font-size: 22px;
    line-height: 1;
}

.tag-artwork-uploader__loading {
    position: absolute;
    inset: 0;
    display: grid;
    place-items: center;
    align-content: center;
    gap: 8px;
    color: oklch(var(--bc) / .72);
    background: oklch(var(--b1) / .78);
    font-size: 12px;
}

.operation-subsection {
    display: grid;
    gap: 8px;
}

.operation-subsection__header {
    color: oklch(var(--bc) / .7);
    font-size: 12px;
    font-weight: 700;
    line-height: 1.4;
}

.lyrics-switch-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
    gap: 10px;
}

.lyrics-switch-card {
    display: block;
    padding: 12px 14px;
    border: 1px solid oklch(var(--bc) / .12);
    border-radius: 8px;
    background: oklch(var(--b1) / .8);
    cursor: pointer;
    transition: border-color .18s ease, background-color .18s ease, box-shadow .18s ease;
}

.lyrics-switch-card:hover,
.lyrics-switch-card:focus-within {
    border-color: oklch(var(--p) / .3);
    background: oklch(var(--b2) / .42);
    box-shadow: 0 1px 4px oklch(var(--bc) / .06);
}

.lyrics-switch-card__head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 8px;
}

.rule-card {
    display: grid;
    gap: 10px;
    padding: 10px;
    border-color: oklch(var(--bc) / .1);
    background: oklch(var(--b2) / .36);
}

.rule-card__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    color: oklch(var(--bc) / .82);
    font-size: 12px;
}

.tidy-row {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    gap: 8px;
    margin-bottom: 8px;
}

.tidy-row--custom {
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr) auto;
}

.operation-info-box {
    padding: 10px 11px;
    border-left: 3px solid oklch(var(--in) / .7);
    border-radius: 0 8px 8px 0;
    color: oklch(var(--bc) / .72);
    background: oklch(var(--in) / .09);
    font-size: 12px;
    line-height: 1.55;
}

.operation-info-box p {
    margin: 0;
}

.operation-info-box p+p {
    margin-top: 3px;
}

.operation-info-box strong,
.operation-warning-box strong {
    display: block;
    margin-bottom: 3px;
    color: oklch(var(--bc) / .88);
    font-size: 12px;
    font-weight: 700;
}

.background-scrape-features {
    display: grid;
    gap: 8px;
}

.background-scrape-feature {
    display: grid;
    gap: 3px;
    padding: 9px 10px;
    border: 1px solid oklch(var(--bc) / .09);
    border-radius: 8px;
    background: oklch(var(--b2) / .28);
}

.background-scrape-feature strong {
    color: oklch(var(--bc) / .86);
    font-size: 13px;
    font-weight: 650;
    line-height: 1.3;
}

.background-scrape-feature small {
    color: oklch(var(--bc) / .62);
    font-size: 12px;
    line-height: 1.45;
}

.operation-warning-box {
    padding: 10px 11px;
    border-left: 3px solid oklch(var(--wa) / .75);
    border-radius: 0 8px 8px 0;
    color: oklch(var(--bc) / .72);
    background: oklch(var(--wa) / .1);
    font-size: 12px;
    line-height: 1.55;
}

.operation-warning-box p {
    margin: 0;
}

.operation-hint-box {
    padding: 9px 10px;
    border: 1px solid oklch(var(--bc) / .09);
    border-radius: 8px;
    color: oklch(var(--bc) / .62);
    background: oklch(var(--b2) / .26);
    font-size: 12px;
    line-height: 1.5;
}

.simple-feature-list {
    display: grid;
    gap: 8px;
}

.simple-feature-item {
    display: grid;
    gap: 3px;
    padding: 9px 10px;
    border: 1px solid oklch(var(--bc) / .09);
    border-radius: 8px;
    background: oklch(var(--b2) / .28);
}

.simple-feature-item strong {
    color: oklch(var(--bc) / .86);
    font-size: 13px;
    font-weight: 650;
    line-height: 1.3;
}

.simple-feature-item small {
    color: oklch(var(--bc) / .62);
    font-size: 12px;
    line-height: 1.45;
}

.language-preview {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 10px;
}

.language-preview>div {
    display: grid;
    gap: 6px;
    padding: 10px;
    border: 1px solid oklch(var(--bc) / .09);
    border-radius: 8px;
    background: oklch(var(--b2) / .28);
}

.language-preview strong {
    color: oklch(var(--bc) / .72);
    font-size: 12px;
    line-height: 1.3;
}

.language-preview span {
    padding: 7px 8px;
    border-radius: 6px;
    background: oklch(var(--b1) / .85);
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
    font-size: 13px;
    line-height: 1.35;
}

.format-custom-value {
    display: inline-flex;
    align-items: center;
    gap: 8px;
}

.format-custom-value .input {
    width: 128px;
}

.format-custom-value span {
    color: oklch(var(--bc) / .62);
    font-size: 12px;
    line-height: 1.4;
}

.auto-mode-options,
.delete-mode-options {
    display: grid;
    gap: 8px;
}

.auto-mode-option,
.delete-mode-option {
    align-items: flex-start;
    padding: 9px 10px;
    border: 1px solid oklch(var(--bc) / .1);
    border-radius: 8px;
    background: oklch(var(--b2) / .28);
}

.auto-mode-option,
.delete-mode-option {
    display: flex;
    gap: 9px;
    cursor: pointer;
}

.auto-mode-option:hover,
.auto-mode-option:focus-within,
.delete-mode-option:hover,
.delete-mode-option:focus-within {
    border-color: oklch(var(--p) / .28);
    background: oklch(var(--p) / .045);
}

.delete-mode-option--danger {
    border-color: oklch(var(--er) / .28);
    background: oklch(var(--er) / .05);
}

.delete-mode-option--danger:hover,
.delete-mode-option--danger:focus-within {
    border-color: oklch(var(--er) / .45);
    background: oklch(var(--er) / .08);
}

.auto-mode-option input,
.delete-mode-option input {
    flex: 0 0 auto;
    margin-top: 2px;
}

.auto-mode-option span,
.delete-mode-option span {
    display: grid;
    min-width: 0;
    gap: 3px;
}

.auto-mode-option strong,
.delete-mode-option strong {
    color: oklch(var(--bc) / .86);
    font-size: 13px;
    font-weight: 650;
    line-height: 1.25;
}

.auto-mode-option small,
.delete-mode-option small {
    color: oklch(var(--bc) / .62);
    font-size: 12px;
    line-height: 1.45;
}

.operation-add-button {
    justify-self: start;
    min-height: 32px;
    padding: 0 11px;
    border: 1px dashed oklch(var(--bc) / .24);
    border-radius: 8px;
    color: oklch(var(--bc) / .76);
    background: transparent;
    cursor: pointer;
    font-size: 12px;
    font-weight: 650;
    transition: border-color .18s ease, color .18s ease, background-color .18s ease, transform .18s ease;
}

.operation-add-button:hover,
.operation-add-button:focus-visible {
    border-color: oklch(var(--p) / .55);
    color: oklch(var(--p));
    background: oklch(var(--p) / .07);
    outline: none;
}

.operation-add-button:active {
    transform: translateY(1px);
}

.operation-field {
    gap: 7px;
}

.operation-field__label {
    color: oklch(var(--bc) / .68);
    font-size: 12px;
    font-weight: 650;
    line-height: 1.2;
}

.operation-field .input,
.operation-field .select,
.operation-field .textarea {
    width: 100%;
}

@media (max-width: 640px) {

    .operation-grid--two,
    .tidy-row,
    .language-preview {
        grid-template-columns: 1fr;
    }

    .template-actions>.btn {
        flex: 1 0 auto;
    }

    .template-actions input {
        flex: 1 1 150px;
        width: auto;
    }

    .tidy-row .btn-circle {
        justify-self: end;
    }
}
</style>
